import './createFieldDrawer.css';
import React,{ useEffect, useState } from 'react';
import { Drawer, Steps } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faClose} from '@fortawesome/free-solid-svg-icons';
import { BasicInfo } from './step1basicInfo';
import { FieldType } from './step2fieldType';
import { Rules } from './step3Rules';
import { useDispatch } from 'react-redux';
import { resetFieldState } from '@src/middleware/redux/reducers/createField.reducer';
import { useSelector } from 'react-redux';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GetProptyById } from '@src/util/query/properties.query';
import { setPropertyTobeEdit } from '@src/middleware/redux/reducers/createField.reducer';
import { setNotification } from '@src/middleware/redux/reducers/notification.reducer';
import Spinner from "@src/components/spinner";
import { topScroll } from '@src/util/topScroll';
import { newTaskInLibrary, updateTaskInLibrary } from "@src/util/mutation/taskInLibrary.mutation"
import { getTaskInLibrary } from '@src/util/query/taskInLibrary.query';
import { v4 as uuidv4 } from 'uuid';
import { LEGACY_GROUP_TENANT_ID } from '@src/util/constants/tenant.constants';


const { Step } = Steps;
const customDot = (dot, { status, index }) => {
  return (
    <div className={status=="process" ? 'custom-dot custom-icon-active' : status=='finish' ? 'custom-dot custom-icon-finished' : `custom-dot`}>
    </div>
  );
};

export const CreateTaskFieldDrawer = ({ selectedCustomer, visible, onClose, taskToBeEdit=null, taskListRefetch, objectType, preselectedSite=null }) => {
  
    // scroll to top if screen is smaller than 1500px
    topScroll();

    const [currentStep, setCurrentStep] = useState(0);
    const [basicInfo, setBasicInfo] = useState(()=>
      preselectedSite?._id
        ? {siteIds: [preselectedSite._id], siteDetails: [preselectedSite]}
        : {}
    );
    const [isNextDisabled, setIsNextDisabled] = useState(true);

    const [fieldType, setFieldType] = useState([{label: '', fieldType: ''}]);

    const [width, setWidth] = useState(false);
    
    // Get both propertyToBeEditId AND labelValueForField from Redux
    const {propertyToBeEditId, labelValueForField} = useSelector(state => state.createFieldReducer);
    const { loading: propertyLoading, data } =  useQuery(GetProptyById, {
      variables: { getPropertyById: (propertyToBeEditId)?.toString() || null },
      skip: !propertyToBeEditId,
      fetchPolicy: 'network-only'
      },
      
    );

    const dispatch = useDispatch();

    const {authenticatedUserDetail} = useSelector(state=>state.userAuthReducer);
    const isLegacyTenant = authenticatedUserDetail?.tenantId === LEGACY_GROUP_TENANT_ID;

    const [getSpecificTask, {data:taskToBeEditDetail, loading: taskToBeEditLoading}] = useLazyQuery(getTaskInLibrary);
    useEffect(()=>{
      if(taskToBeEdit){
        getSpecificTask({
          variables:{
            getTaskInLibraryId: taskToBeEdit
          },
          fetchPolicy: 'network-only'
        });
      }
    },[taskToBeEdit]);


    useEffect(()=>{
      if(taskToBeEditDetail?.getTaskInLibrary?.response && !taskToBeEditLoading){
        const task = taskToBeEditDetail?.getTaskInLibrary?.response;
        setBasicInfo({
          groupId: task?.taskLibraryGroupId,
          groupName: task?.taskGroupDetail?.name,
          siteIds: task?.siteIds || [],
          siteDetails: task?.siteDetails || [],
          label: task?.label,
          description: task?.description,
          flow: task?.flow,
          formId: task?.formId
        });
        setFieldType((task?.fields||[{label: '', fieldType: ''}]));

      }else{
        setFieldType([{label: '', fieldType: ''}])
      }

    },[taskToBeEditDetail?.getTaskInLibrary?.response, taskToBeEditLoading]);

    // Sync fieldType with labelValueForField from Redux when navigating back
    useEffect(() => {
      if (labelValueForField && typeof labelValueForField === 'object' && currentStep==1) {
        setFieldType(prevFieldType => {
          return prevFieldType.map((field, index) => {
            const storedOptions = labelValueForField[index];
            if (storedOptions && Array.isArray(storedOptions)) {
              return {
                ...field,
                options: storedOptions.filter(opt => opt.value)
              };
            }
            return field;
          });
        });
      }
    }, [labelValueForField, currentStep]);
    


    useEffect(() => {
      let shouldDisable = true;
    
      if (currentStep === 0) {
        shouldDisable = !(
          (isLegacyTenant ? basicInfo?.groupId?.length > 0 : basicInfo?.siteIds?.length > 0) &&
          basicInfo?.label?.length > 0
        );
      } else if (currentStep === 1) {
        const hasValidFlow = basicInfo?.flow?.length > 0;
        const hasValidFormId = basicInfo?.flow=="formBase" && basicInfo?.formId?.length > 0;
        const hasValidFieldType = basicInfo?.flow=="field" && fieldType?.every((field) =>
          // field?.label?.length > 0 &&
         field?.fieldType?.length > 0
        );
        shouldDisable = !(hasValidFlow && (hasValidFormId || hasValidFieldType));
      } else {
        shouldDisable = false;
      }
    
      setIsNextDisabled(shouldDisable);
    }, [basicInfo, fieldType, currentStep, isLegacyTenant]);
    
    

    const clearandClose=async()=>{
      await dispatch(setPropertyTobeEdit(null));
      await dispatch(resetFieldState());
      setBasicInfo(null);
      sessionStorage.clear();
      setCurrentStep(0)
      setFieldType([{label: '', fieldType: ''}]);
      setIsNextDisabled(true);
      onClose();
    }


    useEffect(()=>{
      sessionStorage.clear();
      setWidth(false);
    },[]);

    useEffect(()=>{
      if(!propertyToBeEditId){
        dispatch(resetFieldState());
        // preserve the preselected site (if any) instead of wiping basicInfo entirely
        setBasicInfo(preselectedSite?._id && !taskToBeEdit
          ? {siteIds: [preselectedSite._id], siteDetails: [preselectedSite]}
          : null
        );
        sessionStorage.clear();
        setCurrentStep(0)
      }
    },[propertyToBeEditId]);


    const steps = [
      {
        title: 'BASIC INFO',
        component: <BasicInfo selectedCustomer={selectedCustomer} selectedObjectType={objectType}  basicInfo={basicInfo} setWidth={setWidth} visible={visible} setBasicInfo={setBasicInfo} />
      },
      {
        title: 'FIELD TYPE',
        component: <FieldType fieldType={fieldType} setFieldType={setFieldType} basicInfo={basicInfo} setWidth={setWidth} setBasicInfo={setBasicInfo}/>
      },
      {
        title: 'Preview',
        component: <Rules basicInfo={basicInfo} fieldType={fieldType} setWidth={setWidth} />
      }
    ];
  
    const handleNext = (e) => {
      // Sync options from Redux before moving forward
      if (currentStep === 1 && labelValueForField) {
        setFieldType(prevFieldType => {
          return prevFieldType.map((field, index) => {
            const storedOptions = labelValueForField[index];
            if (storedOptions && Array.isArray(storedOptions)) {
              return {
                ...field,
                options: storedOptions.filter(opt => opt.value)
              };
            }
            return field;
          });
        });
      }
      setCurrentStep(currentStep + 1);
    };

   
  
    const handlePrev = () => {
      // Sync options from Redux before moving back
      if (currentStep === 2 && labelValueForField) {
        setFieldType(prevFieldType => {
          return prevFieldType.map((field, index) => {
            const storedOptions = labelValueForField[index];
            if (storedOptions && Array.isArray(storedOptions)) {
              return {
                ...field,
                options: storedOptions.filter(opt => opt.value)
              };
            }
            return field;
          });
        });
      }
      setCurrentStep(currentStep - 1);
    };
  
    // mutation
    const [newTask, {loading, error}] = useMutation(newTaskInLibrary);
    
    const [disableBtn, setDisabledBtn] = useState(false);

    const [updateTask, {loading:updateTaskLoading}] = useMutation(updateTaskInLibrary);

    const handelSubmit= async ()=>{
      setDisabledBtn(true);

      try{

        const field = {
          _id: taskToBeEdit ,
          label: basicInfo?.label,
          ...(isLegacyTenant
            ? {taskLibraryGroupId: basicInfo?.groupId}
            : {siteIds: basicInfo?.siteIds}
          ),
          createdBy: authenticatedUserDetail?.employeeId,
          flow: basicInfo?.flow,
          description: basicInfo?.description,
          fields: basicInfo?.flow=="field" ? fieldType?.map((field)=>({
            ...field,
            label: field?.label,
            fieldType: field?.fieldType,
            key: uuidv4(),
            options: field?.options || [] // Include options in submission
          })) : null,
          formId: basicInfo?.flow=="formBase" ? basicInfo?.formId : null,
          customerId: selectedCustomer?.id
        };

        if(taskToBeEdit){
          await updateTask({
            variables:{
              input: field
            }
          })
        }else{
          await newTask({variables:{input:field}});
        }
        
        clearandClose();
        
        dispatch(setNotification({
          notificationState:true, 
          message:"Task was created successfully",
          error: false,
        }));
  
        await taskListRefetch();
        

      }
      catch(err){

        clearandClose();
        dispatch(setNotification(
          {
            notificationState:true, 
            message: err.message,
            error: true,
          }
        ));

        setDisabledBtn(false);


      }

    };

    return (
        <div>

          <Drawer
            title={(taskToBeEdit? "Update ": "Create ")+"Task"+ (selectedCustomer?.id ? " for (" +selectedCustomer?.name+")" : "")}
            placement="right"
            closable={true}
            onClose={clearandClose}
            closeIcon={<FontAwesomeIcon icon={faClose} onClick={clearandClose} className='close-icon'/>}
            visible={visible}
            width={width ? 900 : 700}
            className='fieldDrawer'
            maskClosable={false}
            mask={true}
            footer={
              <div className='drawer-footer' style={{display:'flex', justifyContent:'space-between'}}>
                  <div>
                  {currentStep > 0 &&
                    <button disabled={false} className={'drawer-outlined-btn'} onClick={handlePrev}>
                      <FontAwesomeIcon style={{marginRight:'0.5em'}} icon={faChevronLeft}/> {'Back'} 
                    </button>
                  }
                    <button className='drawer-btn' onClick={clearandClose} >Cancel</button>
                  </div>
                  
                  {(currentStep < steps.length - 1) && !disableBtn &&
                    <button 
                      id="nextBtn" 
                      className={
                        isNextDisabled
                        ? 'disabled-btn drawer-filled-btn' 
                        : 'drawer-filled-btn'
                      } 
                      onClick={handleNext}
                    >
                      {'Next'} <FontAwesomeIcon className='next-btn-icon' icon={faChevronRight}/>
                    </button>
                  }
                  {currentStep == steps.length - 1 && 
                    <button disabled={loading || disableBtn} onClick={handelSubmit} className={(currentStep ==0 && isNextDisabled) || loading || disableBtn ? ' disabled-btn drawer-filled-btn' : 'drawer-filled-btn'}>
                      {loading || disableBtn? <Spinner/> : taskToBeEdit? 'Update' :'Create'}
                    </button>
                  }

              </div>
            }
          >




            {/* stepper body */}
            {propertyLoading?
              <Spinner />
              :
              
              <>  
                {/* stepper header */}
                <Steps current={currentStep} progressDot={customDot}>
                  {steps.map((step, index) => (
                    <Step key={index} title={step.title} />
                  ))}
                </Steps>
                <div className='stepperBody'>{steps[currentStep].component}</div>
              </>

            }
          </Drawer>

        </div>
      );
      
}